import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { useFacilityCurrency } from "@/components/account/facility/facility-currency";
import {
  FacilityFinancialsClient,
  type FinancialData,
} from "@/components/account/facility/facility-financials-client";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/financials")({
  component: FacilityFinancialsPage,
});

function FacilityFinancialsPage() {
  const { facilityId } = Route.useParams();
  const { i18n } = useLingui();
  const locale = i18n.locale;
  // One currency source for every facility money screen (no fixed RON).
  const { currency, isLoading: currencyLoading } = useFacilityCurrency(facilityId);

  const query = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/financials`],
    queryFn: ({ signal }) =>
      apiFetch.get<FinancialData>(`/api/account/facility/${facilityId}/financials`, { signal }),
    retry: false,
  });

  if (query.isPending || currencyLoading) return <FinancialsSkeleton />;

  // A failed load is tested first: only a 403/404 means "no access".
  if (query.isError || !query.data) {
    const kind = query.isError ? facilityErrorKind(query.error) : "load-failed";
    return (
      <FacilityStateCard
        kind={kind}
        facilityId={facilityId}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
        message={
          kind === "no-access" ? (
            <Trans>Ask the owner or an admin to give you financial-management access.</Trans>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <FacilityFinancialsClient
        initialData={query.data}
        currency={currency}
        locale={locale}
        onRefresh={() => void query.refetch()}
      />
      <AccountSecurityBanner />
    </div>
  );
}

function FinancialsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-48 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}
